/**
 * Admin Flower Order Detail Modal
 *
 * Shows full order information when an admin clicks a row in the flower orders table.
 * Fetches detail from GET /api/admin/flower-orders/{id}, which logs an audit event
 * for PII access accountability (address, recipient phone, sender phone).
 *
 * Sections: Order header, product (with image), pricing breakdown, payment,
 * recipient (PII), event, customer, timestamps.
 *
 * Contextual action buttons:
 * - Cancel Order: visible when the pre-order is cancellable (Pending + None/Authorized)
 * - Retry Capture: visible only for PaymentCaptureFailed orders
 * - Void Authorization: visible only for PaymentAuthorized orders
 * Both require confirmation before executing.
 */

import { useState, useEffect } from 'react';
import {
  Text,
  Group,
  Stack,
  Badge,
  Divider,
  Loader,
  Center,
  Alert,
  Button,
  Image,
  Paper,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AppModal } from '../common/AppModal';
import { useModalZIndex } from '../../hooks/useModalZIndex';
import { FlowerOrderPriceBreakdownList } from '../flowers/FlowerOrderPriceBreakdownList';
import { FlowerOrderPriceLineLabels } from '../../constants/flowerOrderPriceLineLabels';
import {
  IconAlertCircle,
  IconRefresh,
  IconBan,
  IconRobot,
  IconTruckDelivery,
  IconX,
} from '@tabler/icons-react';
import apiClient from '../../services/apiClient';
import { placeFlowerOrderAsAdmin } from '../../services/adminOperationsService';
import { actionColors, brandColors, semanticColors } from '../../config/theme';
import { formatCalendarDate, formatInstantLocalShort } from '../../utils/formatDateTime';
import { FulfillmentStatus, PaymentStatus } from '../../types';
import { OrderStatusBadges } from '../common/OrderStatusBadges';
import {
  canAdminPlaceFlowerOrder,
  canCancelPaidOrder,
  formatRetryingBadgeLabel,
  formatStatusLabel,
  RETRYING_PLACEMENT_BADGE_COLOR,
} from '../../utils/orderStatusDisplay';
import type { AdminFlowerOrder } from '../../pages/admin/AdminFlowerOrdersPage';
import { getStateName } from '../../constants/states';
import { formatFlowerVendorLabel } from '../../constants/flowerVendorIds';
import { DemoAccountPill } from './DemoAccountPill';

interface AdminFlowerOrderDetailModalProps {
  /** The order ID to load details for. Null when modal is closed. */
  orderId: number | null;
  /** Whether the modal is open. */
  opened: boolean;
  /** Callback to close the modal. */
  onClose: () => void;
  /** Called after a successful action (retry capture, void) to refresh the list. */
  onOrderUpdated?: () => void;
}

const formatDate = (dateStr: string | null | undefined): string =>
  formatCalendarDate(dateStr);

const formatDateTime = (dateStr: string | null | undefined): string =>
  formatInstantLocalShort(dateStr);

const formatCurrency = (amount: number | null | undefined): string => {
  if (amount == null) return '--';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

/** A single label-value row in the detail modal. */
const DetailRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Group justify="space-between" wrap="nowrap">
    <Text fw={500} size="sm" style={{ flexShrink: 0 }}>
      {label}
    </Text>
    <div style={{ textAlign: 'right' }}>{children}</div>
  </Group>
);

export const AdminFlowerOrderDetailModal = ({
  orderId,
  opened,
  onClose,
  onOrderUpdated,
}: AdminFlowerOrderDetailModalProps) => {
  const zIndex = useModalZIndex(opened);
  const [order, setOrder] = useState<AdminFlowerOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'retry' | 'void' | 'place' | 'cancel' | null>(null);

  // Fetch detail on open (triggers server-side audit log)
  useEffect(() => {
    if (!opened || !orderId) {
      setOrder(null);
      setError(null);
      setConfirmAction(null);
      return;
    }

    const fetchDetail = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.get<AdminFlowerOrder>(
          `/admin/flower-orders/${orderId}`
        );
        setOrder(response.data);
      } catch {
        setError('Failed to load order details.');
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [opened, orderId]);

  /** Executes retry-capture for PaymentCaptureFailed orders. */
  const handleRetryCapture = async () => {
    if (!order) return;
    setActionLoading(true);

    try {
      await apiClient.post(`/admin/flower-orders/${order.id}/retry-capture`);
      notifications.show({
        title: 'Capture Succeeded',
        message: `Order #${order.id} status updated to Placed.`,
        color: brandColors.success,
      });
      onOrderUpdated?.();
      onClose();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Retry capture failed. Check the Stripe dashboard.';
      notifications.show({
        title: 'Capture Failed',
        message,
        color: brandColors.danger,
      });
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  /**
   * Admin override Place Order Now. For pre-orders the scheduler should already
   * have placed but didn't yet (Pending or PaymentAuthorized, no FloristOne id,
   * delivery date inside FloristOneLimits.MaxDeliveryDaysAhead). The backend
   * applies all three guards; we surface the resulting message verbatim so the
   * operator knows exactly which guard tripped when the call is rejected.
   */
  const handlePlaceOrder = async () => {
    if (!order) return;
    setActionLoading(true);

    try {
      const response = await placeFlowerOrderAsAdmin(order.id);
      notifications.show({
        title: 'Order Placed',
        message: response.message,
        color: brandColors.success,
      });
      onOrderUpdated?.();
      onClose();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Place order failed.';
      notifications.show({
        title: 'Place Order Failed',
        message,
        color: brandColors.danger,
      });
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  /** Cancels a pending pre-order (same rules as end-user cancel). */
  const handleCancelOrder = async () => {
    if (!order) return;
    setActionLoading(true);

    try {
      await apiClient.post(`/admin/flower-orders/${order.id}/cancel`);
      notifications.show({
        title: 'Order Cancelled',
        message: `Flower order #${order.id} has been cancelled.`,
        color: brandColors.success,
      });
      onOrderUpdated?.();
      onClose();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Cancel failed.';
      notifications.show({
        title: 'Cancel Failed',
        message,
        color: brandColors.danger,
      });
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  /** Executes void-authorization for PaymentAuthorized orders. */
  const handleVoidAuthorization = async () => {
    if (!order) return;
    setActionLoading(true);

    try {
      await apiClient.post(`/admin/flower-orders/${order.id}/void-authorization`);
      notifications.show({
        title: 'Authorization Voided',
        message: `Order #${order.id} status updated to Failed.`,
        color: brandColors.success,
      });
      onOrderUpdated?.();
      onClose();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Void failed. Check the Stripe dashboard.';
      notifications.show({
        title: 'Void Failed',
        message,
        color: brandColors.danger,
      });
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  /** Builds the formatted address string. */
  const formatAddress = (): string => {
    if (!order) return '--';
    const parts = [
      order.addressLine1,
      order.addressLine2,
      [order.city, getStateName(order.state, order.country)].filter(Boolean).join(', '),
      order.postalCode,
      order.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join('\n') : '--';
  };

  const showRetryCapture =
    order?.fulfillmentStatus === FulfillmentStatus.Placed
    && order?.paymentStatus === PaymentStatus.CaptureFailed;
  const showVoidAuth =
    order?.fulfillmentStatus === FulfillmentStatus.Pending
    && order?.paymentStatus === PaymentStatus.Authorized;
  // Place Order Now mirrors the scheduler's recovery path. Server enforces
  // the FloristOne 30-day window and VendorOrderId-is-null guards; we
  // only gate on status here so the button is visible whenever the operator
  // could reasonably want it. Placed/CaptureFailed is excluded because those
  // orders WERE placed with the vendor — Retry Capture is the right path.
  const showCancelOrder =
    order != null
    && canCancelPaidOrder(order.fulfillmentStatus, order.paymentStatus);
  const showPlaceOrder =
    order != null
    && !order.vendorOrderId
    && canAdminPlaceFlowerOrder(order.fulfillmentStatus, order.paymentStatus);

  const actionFooter =
    order && (showCancelOrder || showRetryCapture || showVoidAuth || showPlaceOrder) ? (
      <Group justify="flex-end">
        {showCancelOrder && (
          <Button
            leftSection={<IconX size={16} />}
            color={actionColors.cancel}
            variant="light"
            onClick={() => setConfirmAction('cancel')}
          >
            Cancel Order
          </Button>
        )}
        {showPlaceOrder && (
          <Button
            leftSection={<IconTruckDelivery size={16} />}
            color={actionColors.sendNow}
            variant="light"
            onClick={() => setConfirmAction('place')}
          >
            Place Order Now
          </Button>
        )}
        {showRetryCapture && (
          <Button
            leftSection={<IconRefresh size={16} />}
            color={actionColors.resend}
            variant="light"
            onClick={() => setConfirmAction('retry')}
          >
            Retry Capture
          </Button>
        )}
        {showVoidAuth && (
          <Button
            leftSection={<IconBan size={16} />}
            color={actionColors.delete}
            variant="light"
            onClick={() => setConfirmAction('void')}
          >
            Void Authorization
          </Button>
        )}
      </Group>
    ) : null;

  const confirmOpened = confirmAction != null;
  const confirmZIndex = useModalZIndex(confirmOpened);

  const confirmTitle =
    confirmAction === 'cancel'
      ? 'Confirm Cancel Order'
      : confirmAction === 'place'
        ? 'Confirm Place Order Now'
        : confirmAction === 'retry'
          ? 'Confirm Retry Capture'
          : confirmAction === 'void'
            ? 'Confirm Void Authorization'
            : 'Confirm';

  const confirmFooter =
    order && confirmAction === 'cancel' ? (
      <Group justify="flex-end">
        <Button
          variant="default"
          onClick={() => setConfirmAction(null)}
          disabled={actionLoading}
        >
          Keep Order
        </Button>
        <Button color={actionColors.cancel} onClick={handleCancelOrder} loading={actionLoading}>
          Yes, Cancel Order
        </Button>
      </Group>
    ) : order && confirmAction === 'place' ? (
      <Group justify="flex-end">
        <Button
          variant="default"
          onClick={() => setConfirmAction(null)}
          disabled={actionLoading}
        >
          Cancel
        </Button>
        <Button color={actionColors.sendNow} onClick={handlePlaceOrder} loading={actionLoading}>
          Yes, Place Order
        </Button>
      </Group>
    ) : order && confirmAction === 'retry' ? (
      <Group justify="flex-end">
        <Button
          variant="default"
          onClick={() => setConfirmAction(null)}
          disabled={actionLoading}
        >
          Cancel
        </Button>
        <Button color={actionColors.sendNow} onClick={handleRetryCapture} loading={actionLoading}>
          Yes, Capture
        </Button>
      </Group>
    ) : order && confirmAction === 'void' ? (
      <Group justify="flex-end">
        <Button
          variant="default"
          onClick={() => setConfirmAction(null)}
          disabled={actionLoading}
        >
          Cancel
        </Button>
        <Button color={actionColors.delete} onClick={handleVoidAuthorization} loading={actionLoading}>
          Yes, Void
        </Button>
      </Group>
    ) : null;

  return (
    <>
    <AppModal
      id="admin-flower-order-detail"
      opened={opened}
      onClose={onClose}
      zIndex={zIndex}
      title={order ? `Flower Order #${order.id}` : 'Order Details'}
      size="lg"
      footer={actionFooter}
    >
      {loading && (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      )}

      {error && (
        <Alert color={brandColors.danger} icon={<IconAlertCircle size={16} />}>
          {error}
        </Alert>
      )}

      {order && !loading && (
        <Stack gap="md">
          {/* Header */}
          <Group justify="space-between">
            <Group gap="sm">
              <OrderStatusBadges
                fulfillmentStatus={order.fulfillmentStatus}
                paymentStatus={order.paymentStatus}
                size="lg"
              />
              {order.isAutoScheduled && (
                <Badge color="blue" size="sm" variant="outline" leftSection={<IconRobot size={12} />}>
                  AutoSend
                </Badge>
              )}
            </Group>
            {order.vendorOrderId && (
              <Text size="sm" c={semanticColors.text.secondary}>
                Vendor #{order.vendorOrderId}
              </Text>
            )}
          </Group>

          {/* Vendor */}
          <Divider label="Vendor" labelPosition="left" />
          <Stack gap={4}>
            <DetailRow label="Vendor">
              <Text size="sm">{formatFlowerVendorLabel(order.vendorId)}</Text>
            </DetailRow>
            <DetailRow label="Vendor order ID">
              <Text size="sm">{order.vendorOrderId || '--'}</Text>
            </DetailRow>
            {order.lastVendorStatus && (
              <DetailRow label="Last vendor status">
                <Text size="sm">{order.lastVendorStatus}</Text>
              </DetailRow>
            )}
          </Stack>

          {/* Product */}
          <Divider label="Product" labelPosition="left" />
          <Group align="flex-start" gap="md">
            {order.imageUrl && (
              <Image
                src={order.imageUrl}
                alt={order.productName}
                w={100}
                h={100}
                radius="md"
                fit="contain"
              />
            )}
            <Stack gap={4}>
              <Text fw={600}>{order.productName}</Text>
              <Text size="sm" c={semanticColors.text.secondary}>SKU: {order.sku}</Text>
              <Text size="sm">Catalog price: {formatCurrency(order.price)}</Text>
            </Stack>
          </Group>

          {/* Pricing Breakdown — Product → Delivery → Tax (FO tax on both). */}
          <Divider label="Pricing" labelPosition="left" />
          <Paper p="sm" withBorder radius="sm">
            <Stack gap={4}>
              <FlowerOrderPriceBreakdownList
                productUsd={order.price}
                productLabel={FlowerOrderPriceLineLabels.catalogPrice}
                deliveryFeeUsd={order.deliveryFee}
                taxUsd={order.tax}
                totalUsd={
                  order.authorizedAmount
                  ?? order.price
                    + (order.deliveryFee ?? 0)
                    + (order.tax ?? 0)
                }
                totalLabel={
                  order.authorizedAmount != null
                    ? FlowerOrderPriceLineLabels.authorizedTotal
                    : FlowerOrderPriceLineLabels.orderTotal
                }
              />
              {order.commissionAmount != null && (
                <DetailRow label="Commission">
                  <Text size="sm">{formatCurrency(order.commissionAmount)}</Text>
                </DetailRow>
              )}
              {order.capturedAmount != null && (
                <DetailRow label="Captured Amount">
                  <Text size="sm" fw={600} c={brandColors.success}>
                    {formatCurrency(order.capturedAmount)}
                  </Text>
                </DetailRow>
              )}
            </Stack>
          </Paper>

          {/* Tracking (when vendor supports delivery tracking) */}
          {(order.trackingNumber || order.trackingUrl || order.carrier || order.deliveredAtUtc) && (
            <>
              <Divider label="Tracking" labelPosition="left" />
              <Stack gap={4}>
                {order.carrier && (
                  <DetailRow label="Carrier">
                    <Text size="sm">{order.carrier}</Text>
                  </DetailRow>
                )}
                {order.trackingNumber && (
                  <DetailRow label="Tracking number">
                    <Text size="sm">{order.trackingNumber}</Text>
                  </DetailRow>
                )}
                {order.trackingUrl && (
                  <DetailRow label="Tracking URL">
                    <Text
                      size="sm"
                      component="a"
                      href={order.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      c={brandColors.info}
                    >
                      Open tracking
                    </Text>
                  </DetailRow>
                )}
                {order.deliveredAtUtc && (
                  <DetailRow label="Delivered">
                    <Text size="sm">{formatDateTime(order.deliveredAtUtc)}</Text>
                  </DetailRow>
                )}
              </Stack>
            </>
          )}

          {/* Payment */}
          <Divider label="Payment" labelPosition="left" />
          <Stack gap={4}>
            <DetailRow label="Payment Status">
              <Text size="sm">{formatStatusLabel(order.paymentStatus)}</Text>
            </DetailRow>
            <DetailRow label="Fulfillment Status">
              <Group gap="xs">
                <Text size="sm">{formatStatusLabel(order.fulfillmentStatus)}</Text>
                {formatRetryingBadgeLabel(order) && (
                  <Badge size="sm" color={RETRYING_PLACEMENT_BADGE_COLOR} variant="light">
                    {formatRetryingBadgeLabel(order)}
                  </Badge>
                )}
              </Group>
            </DetailRow>
            {order.lastPlacementError && (
              <DetailRow label="Last Placement Error">
                <Text size="xs" c={semanticColors.text.secondary}>
                  {order.lastPlacementError}
                </Text>
              </DetailRow>
            )}
            {order.cardLast4 && (
              <DetailRow label="Card">
                <Text size="sm">•••• {order.cardLast4}</Text>
              </DetailRow>
            )}
            {order.stripePaymentIntentId && (
              <DetailRow label="Stripe PI">
                <Text size="xs" ff="monospace" c={semanticColors.text.secondary}>
                  {order.stripePaymentIntentId}
                </Text>
              </DetailRow>
            )}
          </Stack>

          {/* Recipient (PII) */}
          <Divider label="Recipient" labelPosition="left" />
          <Stack gap={4}>
            <DetailRow label="Name">
              <Text size="sm">{order.recipientName}</Text>
            </DetailRow>
            <DetailRow label="Address">
              <Text size="sm" style={{ whiteSpace: 'pre-line', textAlign: 'right' }}>
                {formatAddress()}
              </Text>
            </DetailRow>
            {order.phone && (
              <DetailRow label="Phone">
                <Text size="sm">{order.phone}</Text>
              </DetailRow>
            )}
            {order.senderPhone && (
              <DetailRow label="Sender Phone">
                <Text size="sm">{order.senderPhone}</Text>
              </DetailRow>
            )}
            {order.specialInstructions && (
              <DetailRow label="Instructions">
                <Text size="sm" style={{ maxWidth: 250, textAlign: 'right' }}>
                  {order.specialInstructions}
                </Text>
              </DetailRow>
            )}
            {order.cardMessage && (
              <DetailRow label="Card Message">
                <Text size="sm" fs="italic" style={{ maxWidth: 250, textAlign: 'right' }}>
                  {order.cardMessage}
                </Text>
              </DetailRow>
            )}
          </Stack>

          {/* Event & Dates */}
          <Divider label="Event & Dates" labelPosition="left" />
          <Stack gap={4}>
            <DetailRow label="Event Type">
              <Text size="sm">{order.eventTypeName}</Text>
            </DetailRow>
            <DetailRow label="Delivery Date">
              <Text size="sm">{formatDate(order.deliveryDate)}</Text>
            </DetailRow>
            {order.originalDeliveryDate && order.originalDeliveryDate !== order.deliveryDate && (
              <DetailRow label="Original Date">
                <Text size="sm" c={semanticColors.text.secondary}>
                  {formatDate(order.originalDeliveryDate)} (adjusted)
                </Text>
              </DetailRow>
            )}
          </Stack>

          {/* Customer */}
          <Divider label="Customer" labelPosition="left" />
          <Stack gap={4}>
            <DetailRow label="Name">
              <Text size="sm">{order.customerName || '--'}</Text>
            </DetailRow>
            <DetailRow label="Email">
              <Text size="sm">{order.customerEmail}</Text>
            </DetailRow>
            {order.isDemoAccount && (
              <DetailRow label="Account">
                <DemoAccountPill size="sm" />
              </DetailRow>
            )}
          </Stack>

          {/* Timestamps */}
          <Divider label="Timestamps" labelPosition="left" />
          <Stack gap={4}>
            <DetailRow label="Created">
              <Text size="sm">{formatDateTime(order.createdAt)}</Text>
            </DetailRow>
          </Stack>
        </Stack>
      )}
    </AppModal>

    <AppModal
      id="admin-flower-order-action-confirm"
      opened={confirmOpened && order != null}
      onClose={() => {
        if (!actionLoading) setConfirmAction(null);
      }}
      zIndex={confirmZIndex}
      title={confirmTitle}
      size="sm"
      bodyHeight="auto"
      footer={confirmFooter}
    >
      {order && confirmAction === 'cancel' && (
        <Text size="sm">
          This will cancel the pre-order
          {order.paymentStatus === PaymentStatus.Authorized
            ? ` and release the ${formatCurrency(order.authorizedAmount)} hold on the customer's card`
            : ''}
          . The linked scheduled item will also be cancelled.
        </Text>
      )}
      {order && confirmAction === 'place' && (
        <Text size="sm">
          This will place the order with Florist One for delivery on{' '}
          {formatDate(order.deliveryDate)} and capture{' '}
          {formatCurrency(order.authorizedAmount ?? order.price)} from the customer's card.
          Florist One only accepts orders within 30 days of delivery.
        </Text>
      )}
      {order && confirmAction === 'retry' && (
        <Text size="sm">
          This will attempt to capture {formatCurrency(order.authorizedAmount)} from the
          customer's card. Proceed?
        </Text>
      )}
      {order && confirmAction === 'void' && (
        <Text size="sm">
          This will release the {formatCurrency(order.authorizedAmount)} hold on the customer's
          card and mark the order as Failed. This cannot be undone.
        </Text>
      )}
    </AppModal>
    </>
  );
};
